import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent } from '@/components/ui/card';
import { Edit3, Save, X } from 'lucide-react';
import { FoodEntry } from '@/hooks/useComprehensiveNutrition';

interface EditFoodEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  entry: FoodEntry | null;
  onSave: (entryId: string, quantity: number, nutritionData: any) => Promise<void>;
}

export const EditFoodEntryModal = ({ isOpen, onClose, entry, onSave }: EditFoodEntryModalProps) => {
  const { t } = useTranslation();
  const [grams, setGrams] = useState(100);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (entry) {
      // Use the entry quantity as grams
      setGrams(Math.round(entry.quantity || 100));
    }
  }, [entry]);

  if (!entry) return null;

  const foodData = entry.food || entry.user_food;

  // Build per-gram nutrition from whatever data we have
  // If entry has a linked food, use its serving data; otherwise derive from the entry's own totals
  let perGramNutrition: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
    sugar: number;
    sodium: number;
  };

  if (foodData) {
    // Linked food: per-gram = per_serving / serving_size
    const servingSize = foodData.serving_size || 1;
    perGramNutrition = {
      calories: foodData.calories_per_serving / servingSize,
      protein: foodData.protein_per_serving / servingSize,
      carbs: foodData.carbs_per_serving / servingSize,
      fat: foodData.fat_per_serving / servingSize,
      fiber: (foodData.fiber_per_serving || 0) / servingSize,
      sugar: (foodData.sugar_per_serving || 0) / servingSize,
      sodium: (foodData.sodium_per_serving || 0) / servingSize,
    };
  } else {
    // AI-logged food: derive per-gram from the entry's stored totals and quantity
    const originalQty = entry.quantity || 1;
    perGramNutrition = {
      calories: (entry.calories || 0) / originalQty,
      protein: (entry.protein || 0) / originalQty,
      carbs: (entry.carbs || 0) / originalQty,
      fat: (entry.fat || 0) / originalQty,
      fiber: (entry.fiber || 0) / originalQty,
      sugar: (entry.sugar || 0) / originalQty,
      sodium: (entry.sodium || 0) / originalQty,
    };
  }

  const newNutrition = {
    calories: Math.round(perGramNutrition.calories * grams),
    protein: Math.round(perGramNutrition.protein * grams),
    carbs: Math.round(perGramNutrition.carbs * grams),
    fat: Math.round(perGramNutrition.fat * grams),
    fiber: Math.round(perGramNutrition.fiber * grams),
    sugar: Math.round(perGramNutrition.sugar * grams),
    sodium: Math.round(perGramNutrition.sodium * grams),
  };

  const foodName = foodData?.name || entry.food_name || t('components.editFoodEntry.unknownFood');

  const handleSave = async () => {
    try {
      setLoading(true);
      await onSave(entry.id, grams, newNutrition);
      onClose();
    } catch (error) {
      console.error('Error saving entry:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (entry) {
      setGrams(Math.round(entry.quantity || 100));
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 className="h-5 w-5" />
            {t('components.editFoodEntry.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Food Info */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-medium">{foodName}</h3>
              {foodData && 'brand' in foodData && foodData.brand && (
                <p className="text-sm text-muted-foreground">{foodData.brand}</p>
              )}
            </CardContent>
          </Card>

          {/* Gram Slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">{t('components.editFoodEntry.portion')}</Label>
              <Input
                type="number"
                value={grams}
                onChange={(e) => {
                  const v = parseInt(e.target.value) || grams;
                  setGrams(Math.max(1, Math.min(2000, v)));
                }}
                onFocus={(e) => e.target.select()}
                className="w-20 h-7 text-center text-sm"
                min={1}
                max={2000}
              />
            </div>
            <div
              className="px-1 py-2"
              style={{ touchAction: 'none' }}
            >
              <Slider
                value={[grams]}
                onValueChange={([value]) => setGrams(Math.round(value))}
                min={1}
                max={1000}
                step={5}
                className="w-full cursor-pointer"
              />
            </div>
          </div>

          {/* Updated Nutrition */}
          <div className="grid grid-cols-5 gap-1 text-xs">
            <div className="text-center p-1.5 bg-muted rounded">
              <div className="font-semibold">{newNutrition.calories}</div>
              <div className="text-muted-foreground text-[10px]">{t('components.editFoodEntry.macroCal')}</div>
            </div>
            <div className="text-center p-1.5 bg-muted rounded">
              <div className="font-semibold">{newNutrition.protein}g</div>
              <div className="text-muted-foreground text-[10px]">{t('components.editFoodEntry.macroProtein')}</div>
            </div>
            <div className="text-center p-1.5 bg-muted rounded">
              <div className="font-semibold">{newNutrition.carbs}g</div>
              <div className="text-muted-foreground text-[10px]">{t('components.editFoodEntry.macroCarbs')}</div>
            </div>
            <div className="text-center p-1.5 bg-muted rounded">
              <div className="font-semibold">{newNutrition.fat}g</div>
              <div className="text-muted-foreground text-[10px]">{t('components.editFoodEntry.macroFat')}</div>
            </div>
            <div className="text-center p-1.5 bg-muted rounded">
              <div className="font-semibold">{newNutrition.fiber}g</div>
              <div className="text-muted-foreground text-[10px]">{t('components.editFoodEntry.macroFiber')}</div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleClose}
              className="flex-1"
              disabled={loading}
            >
              <X className="h-4 w-4 mr-2" />
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleSave}
              className="flex-1"
              disabled={loading}
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? t('components.editFoodEntry.saving') : t('components.editFoodEntry.saveChanges')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
